import { forwardRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useMergedRef from 'hooks/useMergedRef';

import { faCheckCircle, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import ViewField from './ViewField';
import { InputMessageContainer } from './styled/InputCommon.styled';
import * as S from './styled/StringInput.styled';

/**
 * @author Burak Unsal
 * @description StringInput component allows user to enter string values
 * @param {object} props - props of the component.
 * @param {String} [props.id] - id of the input field.
 * @param {String} [props.name] - name of the input field.
 * @param {'input', 'textarea'} [props.component = 'input'] -  The component used for the root node. Either a string to use a DOM element or a component.
 * @param {String} [props.forceNumber] - force to convert the value to number.
 * @param {String} [props.maxlength = 254] - maxlength of the input field.
 * @param {String} [props.successMessage] - success message of the input field.
 * @param {String} [props.errorMessage] - error message of the input field.
 * @param {Boolean} [props.viewMode = false] - viewMode state of the input field.
 * @param {String} [props.placeholder] - placeholder of the input field.
 * @param {Boolean} [props.disabled] - disabled state of the input field.
 * @param {String} [props.className] - class name of the input field.
 * @param {String} [props.value] - value of the global component.
 * @param {Function} [props.setValue] - setValue function for global component.
 * @param {Boolean} [props.allowOnlyNumbers] - if true, the input will allow only numbers.
 * @returns {JSX} StringInput component.
 * @example
 * <StringInput name="name" />
 * @example
 * <StringInput name="name" component="textarea" />
 * @example
 * <StringInput name="name" type="password" />
 * @example
 * <StringInput name="name" type="number" min={0} max={100} />
 * @example
 * <StringInput name="name" viewMode />
 * @example
 * <StringInput name="name" disabled />
 * @example
 * <StringInput name="name" placeholder="Enter your name" />
 * @example
 * <StringInput name="name" className="custom-class" />
 * @example
 * <StringInput value={value} setValue={setValue} />
 */

const StringInput = (props, ref) => {
	const { name = '', viewMode, disabled, ...moreProps } = props;

	return (
		<>
			{viewMode || disabled ? (
				<ViewField disabled={disabled} viewMode={viewMode} name={name} {...moreProps} autoTranslation={false} />
			) : name ? (
				<FormComponent ref={ref} name={name} {...moreProps} />
			) : (
				<GlobalComponent ref={ref} {...moreProps} />
			)}
		</>
	);
};

const FormComponent = forwardRef((props, ref) => {
	const {
		name = '',
		component = 'input',
		forceNumber,
		maxlength = '254',
		errorMessage: errorMessageProp,
		placeholder,
		className,
		successMessage,
		allowOnlyNumbers,
		...restProps
	} = props;
	const { t } = useTranslation();

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;
	const register = formMethods?.register;

	const { ref: formRef, ...formProps } = register(name, {
		setValueAs: (value) =>
			value === '' || value === null ? null : forceNumber && !isNaN(value) ? Number(value) : value
	});
	const refs = useMergedRef(ref, formRef);

	return (
		<>
			<S.Input
				autoComplete="off"
				innerRef={refs}
				tag={component}
				className={`${errorMessage ? 'error ' : ''}${className ?? ''} ${
					name ? `stringinput-class-selector-${name}` : ''
				}`}
				placeholder={placeholder && t(placeholder)}
				maxLength={maxlength}
				onKeyPress={allowOnlyNumbers ? handleOnlyNumbers : undefined}
				{...formProps}
				{...restProps}
			/>
			<Alerts successMessage={successMessage} errorMessage={errorMessage} />
		</>
	);
});

FormComponent.displayName = 'StringInputFormComponent';

const GlobalComponent = forwardRef((props, ref) => {
	const {
		className,
		placeholder,
		errorMessage,
		maxlength = '254',
		component = 'input',
		value,
		setValue,
		forceNumber,
		successMessage,
		allowOnlyNumbers,
		...restProps
	} = props;
	const { t } = useTranslation();

	const handleOnChange = (e) => {
		const value = e.target.value;
		setValue &&
			setValue(value === '' || value === null ? null : forceNumber && !isNaN(value) ? Number(value) : value);
	};

	return (
		<>
			<S.Input
				innerRef={ref}
				tag={component}
				className={`${errorMessage ? 'error ' : ''}${className ?? ''}`}
				placeholder={placeholder && t(placeholder)}
				maxLength={maxlength}
				value={value ?? ''}
				onChange={handleOnChange}
				onKeyPress={allowOnlyNumbers ? handleOnlyNumbers : undefined}
				{...restProps}
			/>
			<Alerts successMessage={successMessage} errorMessage={errorMessage} />
		</>
	);
});

GlobalComponent.displayName = 'StringInputGlobalComponent';

const handleOnlyNumbers = (e) => {
	// Allow special keys
	const specialKeys = [
		'Backspace',
		'Tab',
		'End',
		'Home',
		'ArrowUp',
		'ArrowDown',
		'ArrowLeft',
		'ArrowRight',
		'Delete',
		'Enter',
		'Escape',
		'Insert',
		'PageUp',
		'PageDown',
		'Control',
		'Alt',
		'Shift'
	];

	if (specialKeys.includes(e.key)) {
		return;
	}

	// Allow modifier keys
	const modifierKeys = ['Control', 'Alt', 'Shift'];

	if (modifierKeys.some((key) => e.getModifierState(key))) {
		return;
	}

	// Allow only numbers
	const regex = new RegExp('^\\d+$');
	if (!regex.test(e.key)) {
		e.preventDefault();
	}
};

const Alerts = ({ successMessage, errorMessage }) => (
	<>
		{successMessage && (
			<InputMessageContainer className="string-input-success-message-container">
				<Icon icon={faCheckCircle} color="var(--green)" size="x-small" />
				<Typography variant="caption" fontWeight="500" color="var(--green)" noWrap>
					{successMessage}
				</Typography>
			</InputMessageContainer>
		)}
		{errorMessage && (
			<InputMessageContainer className="error-message-container string-input-error-message-container">
				<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
				<Typography variant="caption" fontWeight="500" color="var(--red)" noWrap>
					{errorMessage}
				</Typography>
			</InputMessageContainer>
		)}
	</>
);

export default forwardRef(StringInput);
